import { useRef, type ReactNode } from "react";
import { Palette as PaletteIcon } from "lucide-react";

import { parseThemeFile } from "../../themeFiles";
import { installCustomTheme, updateCustomTheme } from "../../themeLibrary";
import { toastManager } from "../ui/toast";

/**
 * The import half of the custom-themes row: the file input, the parse, and
 * the install-or-replace flow.
 *
 * Extracted from `ThemeCustomThemes.tsx` so that file stays under the
 * file-size ratchet; behavior is unchanged. Provenance follows the section:
 * the import-over-existing behavior is modeled on upstream's
 * `ThemeImportDialog.tsx` (pingdotgg/t3code, MIT, pin e3e7cc3f).
 *
 * @module
 */

/**
 * Read, parse, install (or replace), and report one theme file. Returns the
 * installed id so the panel can select it, or null when the file was refused.
 */
async function importThemeFile(file: File): Promise<string | null> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    toastManager.add({
      type: "error",
      title: "Could not import theme",
      description: `${file.name} is not a valid JSON theme file.`,
    });
    return null;
  }

  try {
    const theme = parseThemeFile(parsed);
    try {
      installCustomTheme(theme);
    } catch (error) {
      // Same id already installed means replace it, matching upstream's
      // import-over-existing flow rather than refusing the file.
      if (error instanceof Error && /already installed/.test(error.message)) {
        updateCustomTheme(theme);
      } else {
        throw error;
      }
    }
    toastManager.add({
      type: "success",
      title: "Theme imported",
      description: theme.name,
    });
    return theme.id;
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Could not import theme",
      description: error instanceof Error ? error.message : "The theme file is invalid.",
    });
    return null;
  }
}

export function ImportThemeControl(props: {
  readonly onImported: (id: string) => void;
}): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex w-full items-center gap-3 sm:w-auto">
      <input
        accept="application/json,.json"
        aria-label="Import theme file"
        className="sr-only"
        id="theme-file-input"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          // Reset so picking the same file twice still fires a change.
          event.currentTarget.value = "";
          if (file === undefined) return;
          void importThemeFile(file).then((id) => {
            if (id !== null) props.onImported(id);
          });
        }}
        ref={inputRef}
        type="file"
      />
      <button
        aria-label="Import theme file"
        className="inline-flex h-8 items-center gap-1.5 rounded-[var(--control-radius)] border border-border/70 px-3 text-xs font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => inputRef.current?.click()}
        type="button"
      >
        <PaletteIcon aria-hidden className="size-3.5" />
        Import theme file
      </button>
    </div>
  );
}
